import { appPath } from "../appPath";
import { useEffect, useState, type CSSProperties } from "react";

import { customerInstitutionTestLogin, customerTestLogin, platformLogin, platformTestLogin } from "../api/client";

type LandingIconName = "person" | "enterprise" | "vehicle" | "property";
type LandingScenarioIconName = "risk" | "assets" | "legal";
type LandingProcessIconName = "discussion" | "configuration" | "connection" | "delivery";

function LandingServiceIcon({ type }: { type: LandingIconName }) {
  const paths = {
    person: <><circle cx="12" cy="8" r="3" /><path d="M5.5 19c.7-3.8 2.8-5.7 6.5-5.7s5.8 1.9 6.5 5.7" /></>,
    enterprise: <><path d="M4 20V7l8-3 8 3v13" /><path d="M8 10h2m4 0h2M8 14h2m4 0h2M9 20v-3h6v3" /></>,
    vehicle: <><path d="M4 16v-4l2-5h12l2 5v4" /><path d="M3 13h18M7 16v2m10-2v2" /><circle cx="7" cy="13" r="1" /><circle cx="17" cy="13" r="1" /></>,
    property: <><path d="M3 11 12 4l9 7" /><path d="M5.5 10v10h13V10M9 20v-6h6v6" /></>,
  } satisfies Record<LandingIconName, React.ReactNode>;

  return <svg aria-hidden="true" className="landing-service-icon" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" viewBox="0 0 24 24">{paths[type]}</svg>;
}

function LandingScenarioIcon({ type }: { type: LandingScenarioIconName }) {
  const paths = {
    risk: <><path d="M12 3 5 6v5c0 4.6 2.7 8 7 10 4.3-2 7-5.4 7-10V6l-7-3Z" /><path d="m8.5 12 2.2 2.2 4.8-5" /></>,
    assets: <><circle cx="10.5" cy="10.5" r="5.5" /><path d="m15 15 5 5M8 11l2-2 2 2 2-3" /></>,
    legal: <><path d="M12 4v16M6 7h12M8 7 4.5 13h7L8 7Zm8 0-3.5 6h7L16 7ZM8 20h8" /></>,
  } satisfies Record<LandingScenarioIconName, React.ReactNode>;

  return <svg aria-hidden="true" className="landing-scenario-icon" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.6" viewBox="0 0 24 24">{paths[type]}</svg>;
}

function LandingProcessIcon({ type }: { type: LandingProcessIconName }) {
  const paths = {
    discussion: <><path d="M4 5h16v11H9l-5 4V5Z" /><path d="M8 9h8M8 12h5" /></>,
    configuration: <><path d="M4 7h10M18 7h2M4 17h2M10 17h10" /><circle cx="16" cy="7" r="2" /><circle cx="8" cy="17" r="2" /></>,
    connection: <><circle cx="6" cy="12" r="3" /><circle cx="18" cy="6" r="3" /><circle cx="18" cy="18" r="3" /><path d="m8.7 10.7 6.6-3.4m-6.6 6 6.6 3.4" /></>,
    delivery: <><path d="M6 3h9l4 4v14H6V3Z" /><path d="M14 3v5h5m-9 6 2 2 4-4" /></>,
  } satisfies Record<LandingProcessIconName, React.ReactNode>;

  return <svg aria-hidden="true" className="landing-process-icon" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.6" viewBox="0 0 24 24">{paths[type]}</svg>;
}

const landingCapabilities = [
  {
    name: "自然人信息",
    icon: "person",
    brief: "从主体核验到风险识别，统一组织多维个人信息。",
    items: ["身份核验", "婚姻信息", "司法信息", "金融风险", "收入参考"],
  },
  {
    name: "企业信息",
    icon: "enterprise",
    brief: "围绕企业主体、经营与关联关系，形成结构化信息视图。",
    items: ["工商登记", "司法风险", "经营情况", "关联关系", "资产线索"],
  },
  {
    name: "车辆信息",
    icon: "vehicle",
    brief: "名下与指定车辆分开查询，兼顾资产发现与价值判断。",
    items: ["名下车辆", "指定车辆", "车辆状态", "车辆价值参考"],
  },
  {
    name: "不动产信息",
    icon: "property",
    brief: "通过统一订单承载查询与结果交付，便于阅读和后续处理。",
    items: ["名下不动产", "权属信息", "不动产明细", "结构化结果"],
  },
] as const;

const landingScenarios = [
  {
    name: "金融风险管理",
    icon: "risk",
    image: "/assets/scenario-financial-risk.png",
    summary: "贯穿信贷全周期，支持识别主体风险并持续关注变化。",
    uses: ["贷前准入", "贷中监测", "贷后管理"],
  },
  {
    name: "不良资产处置",
    icon: "assets",
    image: "/assets/scenario-distressed-assets.png",
    summary: "围绕资产发现、价值判断与处置优化，为决策提供信息支持。",
    uses: ["资产线索发现", "资产价值评估", "关联关系梳理", "处置路径研判", "处置方案优化"],
  },
  {
    name: "法律服务",
    icon: "legal",
    image: "/assets/scenario-legal-services.png",
    summary: "将主体、风险与资产信息组织成可用线索，辅助案件办理。",
    uses: ["客户尽调", "诉前调查", "诉讼辅助", "执行财产线索整理"],
  },
] as const;

export function isLocalTestHost(hostname: string): boolean {
  return ["127.0.0.1", "localhost", "::1"].includes(hostname);
}

export function LandingPage({
  initialLoginOpen = false,
  testLoginAvailable = typeof window !== "undefined"
    && window.location.protocol === "http:"
    && isLocalTestHost(window.location.hostname),
}: {
  initialLoginOpen?: boolean;
  testLoginAvailable?: boolean;
}) {
  const [loginOpen, setLoginOpen] = useState(initialLoginOpen);
  const [account, setAccount] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeCapability, setActiveCapability] = useState(0);

  useEffect(() => {
    if (!loginOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setLoginOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [loginOpen]);

  useEffect(() => {
    const rotation = window.setInterval(() => {
      setActiveCapability((current) => (current + 1) % landingCapabilities.length);
    }, 3000);
    return () => window.clearInterval(rotation);
  }, []);

  function moveCapability(direction: number) {
    setActiveCapability((current) => (current + direction + landingCapabilities.length) % landingCapabilities.length);
  }

  async function submitLogin(event: React.FormEvent) {
    event.preventDefault();
    setLoginError(false);
    setSubmitting(true);
    try {
      const identity = await platformLogin(account, password);
      if (identity.destination === "ADMIN") window.location.assign(appPath("/admin"));
      else if (identity.destination === "FINANCIAL") window.location.assign(appPath("/finance"));
      else window.location.assign(appPath("/portal"));
    } catch {
      setLoginError(true);
      setSubmitting(false);
    }
  }

  async function submitTestLogin() {
    setLoginError(false);
    setSubmitting(true);
    try {
      const identity = await platformTestLogin();
      if (identity.destination === "ADMIN") window.location.assign(appPath("/admin"));
    } catch {
      setLoginError(true);
      setSubmitting(false);
    }
  }

  async function submitCustomerTestLogin() {
    setLoginError(false);
    setSubmitting(true);
    try {
      await customerTestLogin();
      window.location.assign(appPath("/portal"));
    } catch {
      setLoginError(true);
      setSubmitting(false);
    }
  }

  async function submitInstitutionTestLogin() {
    setLoginError(false);
    setSubmitting(true);
    try {
      await customerInstitutionTestLogin();
      window.location.assign(appPath("/portal"));
    } catch {
      setLoginError(true);
      setSubmitting(false);
    }
  }

  return (
    <main className="landing-page">
      <div className="landing-shell">
        <nav className="landing-nav" aria-label="主导航">
          <a className="landing-brand" href="#home" aria-label="返回首页">中承信合数据服务平台</a>
          <div className="landing-links">
            <a href="#services">产品服务</a>
            <a href="#scenarios">适用场景</a>
            <a href="#process">接入流程</a>
            <a href="#contact">联系我们</a>
          </div>
          <button className="landing-login" type="button" onClick={() => setLoginOpen(true)}>平台入口</button>
        </nav>

        <div className="landing-scroll">
          <section className="landing-hero" id="home">
            <video
              className="landing-hero-video"
              src={appPath("/assets/landing-hero.v2.mp4")}
              poster={appPath("/assets/landing-hero-poster.v2.webp")}
              preload="metadata"
              autoPlay
              muted
              loop
              playsInline
              aria-hidden="true"
            />
            <div className="landing-hero-shade" aria-hidden="true" />
            <div className="landing-product-showcase" id="services">
              <div className="landing-product-heading">
                <p>产品与服务</p>
                <h1>多维信息服务</h1>
                <span>覆盖自然人、企业、车辆与不动产，为不同业务环节提供结构化信息支持。</span>
              </div>
              <div className="landing-service-deck" aria-label="产品服务轮播" aria-roledescription="carousel">
                <div className="landing-depth-stage" aria-live="polite">
                  {landingCapabilities.map((item, index) => {
                    const depthIndex = (index - activeCapability + landingCapabilities.length) % landingCapabilities.length;
                    return (
                      <article
                        aria-hidden={depthIndex !== 0}
                        className={`landing-depth-card${depthIndex === 0 ? " is-active" : ""}`}
                        key={item.name}
                        role="tabpanel"
                        style={{ "--depth-index": depthIndex, "--depth-order": landingCapabilities.length - depthIndex } as CSSProperties}
                      >
                        <div className="landing-depth-card-heading">
                          <div className="landing-depth-card-icon"><LandingServiceIcon type={item.icon} /></div>
                          <h2>{item.name}</h2>
                        </div>
                        <p>{item.brief}</p>
                        <ul>{item.items.map((detail) => <li key={detail}>{detail}</li>)}</ul>
                      </article>
                    );
                  })}
                </div>
                <div className="landing-carousel-controls">
                  <button aria-label="上一个产品" className="landing-carousel-arrow" onClick={() => moveCapability(-1)} type="button">‹</button>
                  <div className="landing-carousel-indicators" role="tablist" aria-label="选择产品">
                    {landingCapabilities.map((item, index) => (
                      <button
                        aria-label={`切换至${item.name}`}
                        aria-selected={activeCapability === index}
                        className={`landing-carousel-indicator${activeCapability === index ? " is-active" : ""}`}
                        key={item.name}
                        onClick={() => setActiveCapability(index)}
                        role="tab"
                        type="button"
                      />
                    ))}
                  </div>
                  <button aria-label="下一个产品" className="landing-carousel-arrow" onClick={() => moveCapability(1)} type="button">›</button>
                </div>
              </div>
            </div>
          </section>

          <section className="landing-contexts" id="scenarios" aria-label="适用场景">
            <div className="landing-section-heading landing-centered-heading landing-compact-heading">
              <h2>三大应用场景</h2>
            </div>
            <div className="landing-scenario-grid">
              {landingScenarios.map((scenario) => (
                <article key={scenario.name}>
                  <img className="landing-scenario-image" src={appPath(scenario.image)} alt="" />
                  <div className="landing-scenario-overlay" aria-hidden="true" />
                  <div className="landing-scenario-badge"><LandingScenarioIcon type={scenario.icon} /></div>
                  <div className="landing-scenario-content">
                    <h3>{scenario.name}</h3>
                    <p>{scenario.summary}</p>
                    <ul>{scenario.uses.map((item) => <li key={item}>{item}</li>)}</ul>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="landing-process" id="process">
            <div className="landing-section-heading landing-centered-heading landing-compact-heading"><h2>产品接入流程</h2><p>根据业务场景选择合适服务，支持通过工作台或接口使用。</p></div>
            <ol>
              <li><span><LandingProcessIcon type="discussion" /></span><b>需求沟通</b><p>明确业务场景、查询对象与结果需求。</p></li>
              <li><span><LandingProcessIcon type="configuration" /></span><b>产品与权限配置</b><p>组合相应信息服务，按机构配置可用权限。</p></li>
              <li><span><LandingProcessIcon type="connection" /></span><b>工作台或接口接入</b><p>选择直接使用工作台，或完成系统接口对接。</p></li>
              <li><span><LandingProcessIcon type="delivery" /></span><b>查询及结果交付</b><p>发起查询，在统一页面查看和管理结果。</p></li>
            </ol>
          </section>

          <section className="landing-compliance landing-contact" id="contact">
            <div className="landing-contact-copy">
              <h2>联系我们</h2>
              <p>产品咨询、方案沟通或合作需求，请联系对应商务人员。</p>
            </div>
            <figure className="landing-qr-card" aria-label="商务微信二维码">
              <img src={appPath("/assets/business-wechat-qr.jpg")} alt="商务微信二维码" />
              <figcaption>商务微信</figcaption>
            </figure>
          </section>

          <footer className="landing-footer">
            <strong>中承信合数据服务平台</strong>
            <div className="landing-footer-legal">
              <span>© 2026 中承信合数据科技有限公司</span>
              <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">京ICP备2026061701号-1</a>
            </div>
          </footer>
        </div>
      </div>
      {loginOpen && (
        <div
          className="landing-login-layer"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setLoginOpen(false);
          }}
        >
          <form className="landing-login-dialog" role="dialog" aria-modal="true" onSubmit={submitLogin}>
            <input
              aria-label="账户"
              autoComplete="username"
              autoFocus
              className={loginError ? "is-error" : ""}
              placeholder="账户"
              value={account}
              onChange={(event) => setAccount(event.target.value)}
            />
            <input
              aria-label="密码"
              autoComplete="current-password"
              className={loginError ? "is-error" : ""}
              placeholder="密码"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <button type="submit" disabled={submitting}>登录</button>
            {testLoginAvailable && (
              <>
                <button
                  className="landing-test-login"
                  type="button"
                  disabled={submitting}
                  onClick={() => void submitCustomerTestLogin()}
                >
                  测试：自动登录客户账户
                </button>
                <button
                  className="landing-test-login"
                  type="button"
                  disabled={submitting}
                  onClick={() => void submitInstitutionTestLogin()}
                >
                  测试：进入通用机构客户后台
                </button>
                <button
                  className="landing-test-login"
                  type="button"
                  disabled={submitting}
                  onClick={() => void submitTestLogin()}
                >
                  测试：自动登录平台管理端
                </button>
              </>
            )}
          </form>
        </div>
      )}
    </main>
  );
}
